import { getAuthUser } from "../../lib/effectiveAuthUser";
import * as Sentry from "@sentry/react";
import { EditorContent } from "@tiptap/react";
import { useEffect, useMemo, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { useLocation } from "react-router-dom";
import styled from "styled-components/macro";
import useSWR from "swr/immutable";
import ReadOnlyEditor from "../../lib/ReadOnlyEditor";
import { editorTypographyStyles } from "../TiptapDocumentSB/EditorContainer/editorContentStyles";
import { PARAGRAPH_SPACING } from "../../../../src/shared/constants.js";
import DisplayName from "../../models/DisplayName.js";
import Subscription from "../../models/Subscription.js";
import { useAppStore } from "../../stores/appStore.js";
import { useCommentStore } from "../../stores/commentStore";
import Comment from "../Comment/Comment.js";
import { filterCommentsByType } from "../TiptapDocumentSB/CommentSidebar/commentStatus";
import NewComment from "../Comment/NewComment.js";
import { HomepageLink } from "../Homepage/HomepageLink";
import Logos from "../Logos/index.js";
import { TiptapErrorBoundary } from "../TiptapErrorBoundary";
import { useToast } from "../ToastWrapper/index.js";
import CommentsBar from "./CommentsBar";
import { commentsManager } from "./CommentsManager";
import { Layout } from "./Layout";
// import UserStatus from "./UserStatus";
import { useComments } from "../../hooks/useComments";
import useEditPluginInstallations from "../../hooks/useEditPluginInstallations";
import { sendAppEvent } from "../../Utils.js";
import { Button } from "../Button/index.js";
import { useWebSocket } from "../SocketHost/WebSocketProvider";
import DesktopOnboarding from "./DesktopOnboarding";
import DesktopOnboardingTooltip from "./DesktopOnboardingTooltip";
import MobileOnboarding from "./MobileOnboarding";
import { resolveShareLinkEditorSelection } from "./resolveShareLinkSelection";
import { authenticateCommenter } from "./authenticateCommenter";
function fetcher(url) {
  return fetch(url, { headers: { accept: "application/json" } }).then((response) => {
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Not Found");
      }

      throw new Error("Error: something went wrong");
    }

    const contentType = response.headers.get("content-type");

    if (!contentType || !contentType.includes("application/json")) {
      throw new TypeError("Error: document wasn't formatted correctly");
    }

    return response.json();
  });
}

export function ShareLinkPage({ user, loadedUser, openLogin, authRedirectResult, authRedirectResultError }) {
  const storedComments = useCommentStore((state) => state.comments);
  // The store retains resolved rows for the author's status tabs. Shared pages
  // only show open threads, including immediately after a local resolve.
  const comments = useMemo(() => filterCommentsByType(storedComments, "open"), [storedComments]);
  const [canComment, setCanComment] = useState(false);
  const { installations } = useEditPluginInstallations();

  const { setToast } = useToast();
  const { registerHandler, unregisterHandler, isConnected, connect, disconnect } = useWebSocket();
  const setSetToast = useCommentStore((state) => state.setSetToast);
  const setDOMDocument = useCommentStore((state) => state.setDOMDocument);
  const commenter = useCommentStore((state) => state.commenter);
  const setCommenter = useCommentStore((state) => state.setCommenter);
  const displayName = useCommentStore((state) => state.displayName);
  const setDisplayName = useCommentStore((state) => state.setDisplayName);
  const anonUser = useAppStore((state) => state.anonUser);
  const setTreatAsAnonymous = useCommentStore((state) => state.setTreatAsAnonymous);
  const setIsDocumentOwner = useCommentStore((state) => state.setIsDocumentOwner);
  const setIsSharedDocument = useCommentStore((state) => state.setIsSharedDocument);

  // debugging
  const setAnnotationTimeout = useCommentStore((state) => state.setAnnotationTimeout);

  // Highlighting + New Comment Box
  const setNewCommentBoxLocationY = useCommentStore((state) => state.setNewCommentBoxLocationY);
  const showNewCommentBox = useCommentStore((state) => state.showNewCommentBox);
  const setShowNewCommentBox = useCommentStore((state) => state.setShowNewCommentBox);
  const createNewAnnotationId = useCommentStore((state) => state.createNewAnnotationId);

  // New Comment Form Fields
  const [newComment, setNewComment] = useState("");
  const [forceOpenCommentId, setForceOpenCommentId] = useState(null);

  // One-shot: keep `forceOpen` true for the first paint so `Comment` can open the reply box,
  // then clear so remounts / list reorders do not permanently force `forceOpen`.
  useEffect(() => {
    if (!forceOpenCommentId) return undefined;
    const id = forceOpenCommentId;
    const t = window.setTimeout(() => {
      setForceOpenCommentId((current) => (current === id ? null : current));
    }, 0);
    return () => window.clearTimeout(t);
  }, [forceOpenCommentId]);

  // Debounce mouseup + synthetic touchend on hybrid devices.
  const commentTriggerLockRef = useRef(0);
  const mouseDownInEditorRef = useRef(false);

  // Filtering
  const [filtering, setFiltering] = useState(false);
  const [filteredComments, setFilteredComments] = useState(comments);

  // Active Comment on Hover over Annotation Marks
  const setHoveredAnnotationIds = useCommentStore((state) => state.setHoveredAnnotationIds);
  const setActiveAnnotationIds = useCommentStore((state) => state.setActiveAnnotationIds);
  const activeAnnotationIds = useCommentStore((state) => state.activeAnnotationIds);

  // Mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileTextSelected, setMobileTextSelected] = useState(false);
  const [mobileTextSelectedLocationY, setMobileTextSelectedLocationY] = useState(0);

  // Onboarding
  const [showMobileOnboarding, setShowMobileOnboarding] = useState(false);
  const [showDesktopOnboarding, setShowDesktopOnboarding] = useState(false);
  const [showDesktopOnboardingTooltip, setShowDesktopOnboardingTooltip] = useState(false);
  const [bigEnoughToShowTooltip, setBigEnoughToShowTooltip] = useState(window.innerWidth > 1200);

  const location = useLocation();
  const url = location.pathname.replace("app", "api");

  const { data: sharedDoc, error: sharedDocError, isLoading: sharedDocLoading } = useSWR(url, fetcher);
  const editor = useCommentStore((state) => state.tiptapEditor);
  const setTiptapEditor = useCommentStore((state) => state.setTiptapEditor);
  const [readOnlyEditorInstance, setReadOnlyEditorInstance] = useState(null);

  // Set isDocumentOwner when we know whether the current user is the author
  useEffect(() => {
    if (user && sharedDoc) {
      setIsDocumentOwner(user.uid === sharedDoc.authorId);
    } else {
      setIsDocumentOwner(false);
    }
  }, [user, sharedDoc, setIsDocumentOwner]);

  useEffect(() => {
    if (sharedDoc) {
      setIsSharedDocument(true);
    }
    return () => {
      setIsSharedDocument(false);
    };
  }, [sharedDoc, setIsSharedDocument]);

  const setMergeAccount = useAppStore((state) => state.setMergeAccount);
  const [showOverlay, setShowOverlay] = useState(false);
  const mutateComments = useCommentStore((state) => state.mutateComments);

  // Use the comments hook
  useComments({
    isAuthor: commenter?.uid === sharedDoc?.authorId,
    documentId: sharedDoc?.documentId,
    userId: commenter?.uid,
  });

  const handleSignUp = () => {
    sendAppEvent("CleanCopy - Clicked Sign Up From Warning Ad", {
      documentId: sharedDoc.documentId,
      numberOfComments: comments.length,
      displayName: displayName,
    });
    setMergeAccount(true);
    localStorage.setItem("redirectLink", window.location.href);
    setShowOverlay(true);
    openLogin();
  };

  const editorHandlers = {
    annotations: {
      onHover: (annotationIds) => {
        setHoveredAnnotationIds(annotationIds);
      },
      onLeave: (_annotationIds) => {
        setHoveredAnnotationIds([]);
      },
      onClick: (annotationIds) => {
        setActiveAnnotationIds(annotationIds);
      },
      commentsManager: commentsManager,
    },
  };

  // So we know where to look for annotation nodes
  useEffect(() => {
    setDOMDocument(document);
    setSetToast(setToast);
  }, []);

  useEffect(() => {
    if (!sharedDoc) return;
    registerHandler("refetchComments", (msg) => {
      if (msg.payload.documentId === sharedDoc.documentId) {
        mutateComments();
      }
    });
    return () => {
      unregisterHandler("refetchComments");
    };
  }, [registerHandler, unregisterHandler, sharedDoc, mutateComments]);

  useEffect(() => {
    if (authRedirectResultError) {
      if (authRedirectResultError?.code === "auth/account-exists-with-different-credential") {
        setToast("You already have an account with this email. Please sign in with that account instead.");
      } else {
        setToast("Something went wrong while signing you in.");
      }
      Sentry.captureMessage("Sign in with redirect error on ShareLinkPage", {
        tags: {
          user: anonUser?.uid,
          email: authRedirectResultError.email,
          authRedirectResultError: authRedirectResultError,
          authRedirectResult: authRedirectResult,
        },
      });
    }
  }, [authRedirectResultError]);

  useEffect(() => {
    if (showDesktopOnboardingTooltip && showNewCommentBox) {
      setShowDesktopOnboardingTooltip(false);
    } else if (showDesktopOnboardingTooltip && !showNewCommentBox) {
      setShowDesktopOnboardingTooltip(true);
    }
  }, [showNewCommentBox]);

  useEffect(() => {
    if (!sharedDoc || !sharedDoc.commentsEnabled) return;
    // Resolving the last visible thread does not make this a first-time commenter.
    const showOnboarding = loadedUser && (!commenter || commenter.isAnonymous) && storedComments.length === 0;
    if (isMobile && showOnboarding) {
      setShowDesktopOnboarding(false);
      setShowDesktopOnboardingTooltip(false);
      setShowMobileOnboarding(true);
    } else if (!isMobile && showOnboarding && bigEnoughToShowTooltip && !showNewCommentBox) {
      setShowDesktopOnboardingTooltip(true);
      setShowMobileOnboarding(false);
      setShowDesktopOnboarding(false);
    } else if (!isMobile && showOnboarding && !bigEnoughToShowTooltip) {
      setShowDesktopOnboardingTooltip(false);
      setShowMobileOnboarding(false);
      setShowDesktopOnboarding(true);
    } else {
      setShowMobileOnboarding(false);
      setShowDesktopOnboarding(false);
      setShowDesktopOnboardingTooltip(false);
    }
  }, [isMobile, loadedUser, commenter, storedComments, sharedDoc, bigEnoughToShowTooltip, showNewCommentBox]);

  // initialize the editor
  useEffect(() => {
    if (sharedDoc && !editor) {
      const newEditor = new ReadOnlyEditor({ documentId: sharedDoc.documentId, editorHandlers, user: commenter, showError: setToast, sharedDoc });
      let rebuildFired = false;
      /*
       * The shared-document reader has a different editor lifecycle from the author page, but uses
       * the same comment store. Do not interpret its initially empty ProseMirror document as an
       * authoritative absence of anchors until the provider has synced. This one-shot callback
       * refreshes the index and removes itself.
       */
      const markAnnotationIndexReady = () => {
        newEditor.provider?.off?.("synced", markAnnotationIndexReady);
        useCommentStore.getState().refreshDocumentAnnotationIndex({ markReady: true });
      };
      /*
       * The API-backed static-document fallback rebuilds a fully populated TipTap instance rather
       * than waiting for a live provider sync. Mark that replacement ready immediately and remove the
       * pending provider listener. Otherwise valid shared-page comments could remain in unknown-index
       * mode after a rebuild.
       */
      newEditor.onEditorRebuild = (newTiptap) => {
        rebuildFired = true;
        newEditor.provider?.off?.("synced", markAnnotationIndexReady);
        setTiptapEditor(newTiptap, { documentSynced: true });
        if (sharedDoc.commentsEnabled) {
          commentsManager.init(newTiptap.view.dom, document, -376);
        }
      };
      newEditor.init().then(() => {
        if (!rebuildFired) {
          /*
           * Handle both ordering cases: subscribe before attaching the editor, then check whether sync
           * already happened. Attachment resets index readiness; the explicit already-synced check
           * prevents missing a sync event that occurred during init. Existing CommentsManager
           * initialization still handles positioning; it is no longer the visibility source of truth.
           */
          newEditor.provider?.on?.("synced", markAnnotationIndexReady);
          setTiptapEditor(newEditor.tiptap);
          if (newEditor.provider?.synced === true) {
            markAnnotationIndexReady();
          }
          if (sharedDoc.commentsEnabled) {
            commentsManager.init(newEditor.tiptap.view.dom, document, -376);
          }
        }
        setReadOnlyEditorInstance(newEditor);
      });

      useCommentStore.setState({ documentId: sharedDoc.documentId });
    }
  }, [sharedDoc]);

  useEffect(() => {
    if (activeAnnotationIds.length > 0 || showNewCommentBox) {
      setShowOverlay(true);
    } else {
      setShowOverlay(false);
    }
  }, [activeAnnotationIds, showNewCommentBox]);

  useEffect(() => {
    if (commenter) {
      if (isConnected) {
        disconnect();
      }
      connect(commenter);
    }
    return () => {
      disconnect();
    };
  }, [commenter, isConnected, connect, disconnect]);

  // initialize the user
  useEffect(() => {
    if (!loadedUser || !sharedDoc || !sharedDoc.commentsEnabled) {
      setCanComment(false);
      return;
    }

    let currentUser = null;

    async function initializeUser() {
      currentUser = getAuthUser();

      if (!currentUser) {
        const anonymousName = await DisplayName.getRandom(sharedDoc.documentId);
        setDisplayName(anonymousName);
        setTreatAsAnonymous(true);
        setCanComment(true);
        return;
      }

      setCanComment(true);
      setCommenter(currentUser);
      const sub = await Subscription.fetchFromUserId(currentUser.uid);
      if (!sub) {
        setTreatAsAnonymous(true);
      }
      getOrAssignDisplayName();
    }

    async function getOrAssignDisplayName() {
      let userDisplayName = await DisplayName.get();
      if (!userDisplayName && !displayName) {
        userDisplayName = await DisplayName.assignRandom(sharedDoc.documentId);
        setDisplayName(userDisplayName);
      } else if (!userDisplayName && displayName) {
        // a user was just logged in anonymously so we update their displayName
        await DisplayName.update(displayName);
      } else {
        setDisplayName(userDisplayName);
      }
    }

    initializeUser().then(() => {
      sendAppEvent("CleanCopy - Share Page Loaded", {
        documentId: sharedDoc.documentId,
        commenterId: currentUser?.uid,
        isAnonymous: currentUser?.isAnonymous,
      });
    });
  }, [user, sharedDoc, loadedUser, anonUser]);

  // Add resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setBigEnoughToShowTooltip(window.innerWidth > 1200);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (readOnlyEditorInstance) {
      readOnlyEditorInstance.user = commenter;
    }
  }, [commenter]);

  // Handle Mobile Text Selection
  useEffect(() => {
    if (!isMobile || !sharedDoc || !sharedDoc.commentsEnabled) return;
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      const editorElement = editor?.view?.dom;

      if (!selection || selection.rangeCount === 0 || !editorElement) return;

      // Get the common ancestor of the selection
      const range = selection.getRangeAt(0);

      const container = range.commonAncestorContainer;

      // Check if the selection is within the editor
      const isWithinEditor = editorElement.contains(container) || container === editorElement;

      // Only set mobile text selected if selection is within editor and not empty
      const hasValidSelection = isWithinEditor && selection.toString().trim().length > 0;

      setMobileTextSelected(hasValidSelection);
      setMobileTextSelectedLocationY(range.getBoundingClientRect().bottom + 10);
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, [isMobile, editor]);

  // Handle Desktop Text Selection (document-level mouseup)
  // Using a document-level listener ensures we capture mouseup even when the
  // user drags upward and releases above/outside the EditorContent element.
  useEffect(() => {
    if (isMobile || !sharedDoc?.commentsEnabled || !editor) return;

    const handleMouseDown = (e) => {
      // Only track mousedowns that start inside the editor
      const editorDom = editor.view.dom;
      mouseDownInEditorRef.current = editorDom.contains(e.target);
    };

    const handleMouseUp = async () => {
      // Only trigger comment creation if the mousedown started in the editor.
      // This prevents stale editor selections from spawning comments when the
      // user clicks elsewhere (comments sidebar, toolbar, etc.).
      if (!mouseDownInEditorRef.current) return;
      mouseDownInEditorRef.current = false;

      if (!canComment) return;

      // Wait one frame for selection to finalize
      await new Promise((r) => requestAnimationFrame(r));

      // Resolve the selection from the DOM — in read-only mode ProseMirror
      // does not reliably update state.selection for backward (bottom-to-top)
      // drags, and normalized Range endpoints resolve backward whole-paragraph
      // selections to block-boundary positions that /api/annotations/new
      // rejects. TextSelection.between clamps to valid text positions.
      const resolvedSelection = resolveShareLinkEditorSelection(editor.view, editor.state.doc);
      if (!resolvedSelection) return;
      const { from, to } = resolvedSelection;

      // Duplicate-trigger lock (same as existing)
      const now = Date.now();
      if (now - commentTriggerLockRef.current < 500) return;
      commentTriggerLockRef.current = now;

      // Position the comment box from the live selection rect, before the
      // optimistic annotation re-renders the selected range
      let bottom;
      const domSel = window.getSelection();
      if (domSel && domSel.rangeCount > 0) {
        const rect = domSel.getRangeAt(0).getBoundingClientRect();
        if (rect && rect.bottom) bottom = rect.bottom;
      }
      if (bottom == null) {
        const startCoords = editor.view.coordsAtPos(from);
        const endCoords = editor.view.coordsAtPos(to);
        bottom = Math.max(startCoords.bottom, endCoords.bottom);
      }

      // Create the annotation and open the composer immediately — persisting
      // costs up to two network round-trips (lazy anonymous sign-in + POST)
      // and shouldn't block the box appearing. Rolled back below on failure.
      const annotationId = createNewAnnotationId();
      const selection = { from, to };
      setOptimisticAnnotation(annotationId, selection);

      // Only reset on a fresh open — preserve any in-progress draft if the box
      // was already showing.
      if (!showNewCommentBox) setNewComment("");
      setNewCommentBoxLocationY(bottom);
      setShowNewCommentBox(true);

      // Debugging timeout for disappearing annotations
      const timeout = setTimeout(() => {
        const annotation = document.querySelector(`[data-annotation-id*="${annotationId}"]`);
        if (!annotation) {
          const error = new Error("Annotation Disappeared");
          Sentry.captureException(error, {
            tags: {
              annotationId,
              documentId: sharedDoc.documentId,
              createComment: true,
              shareLinkPage: true,
            },
          });
        }
      }, 5000);

      setAnnotationTimeout(annotationId, timeout);

      persistNewCommentAnnotation({ annotationId, selection }).catch((error) => {
        // _unsetAnnotation also clears the debug timeout
        useCommentStore.getState()._unsetAnnotation(annotationId);
        if (useCommentStore.getState().newAnnotationId === annotationId) {
          setShowNewCommentBox(false);
        }
        setToast("Could not save annotation. Please try again.");
        Sentry.captureException(error, {
          tags: {
            annotationId,
            documentId: sharedDoc.documentId,
            createComment: true,
            shareLinkPage: true,
            persistAnnotation: true,
          },
        });
      });
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isMobile, editor, sharedDoc, canComment, showNewCommentBox]);

  // Since the connection is readonly, this wont actually stick
  // until the backend processes the annotation
  const setOptimisticAnnotation = (annotationId, selection) => {
    try {
      editor.setEditable(true);
      editor.chain().setTextSelection({ from: selection.from, to: selection.to }).setAnnotation({ annotationId }).run();
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          annotationId,
          documentId: sharedDoc.documentId,
          shareLinkPage: true,
        },
      });
    } finally {
      editor.setEditable(false);
    }
  };

  const ensureAuthenticatedCommenter = async () => {
    const authUser = await authenticateCommenter();
    setCommenter(authUser);
    return authUser;
  };

  // Registered with the comment store so a cancel while the sign-in/POST is still
  // in flight orders the server delete after the create instead of racing it.
  const persistNewCommentAnnotation = ({ annotationId, selection }) =>
    useCommentStore.getState().persistDraftAnnotation({
      annotationId,
      documentId: sharedDoc.documentId,
      from: selection.from,
      to: selection.to,
      authenticate: ensureAuthenticatedCommenter,
    });

  // HANDLERS
  const commentHandlers = {
    createMobileComment: async () => {
      const selection = editor.view.state.selection;
      if (editor.state.doc.textBetween(selection.from, selection.to, " ").trim() === "") {
        return;
      }
      setMobileTextSelected(false);

      // Create the annotation and open the composer immediately — persisting
      // costs up to two network round-trips (lazy anonymous sign-in + POST)
      // and shouldn't block the box appearing. Rolled back below on failure.
      const annotationId = createNewAnnotationId();
      setOptimisticAnnotation(annotationId, selection);

      // Only reset on a fresh open — preserve any in-progress draft if the box
      // was already showing.
      if (!showNewCommentBox) setNewComment("");
      setShowNewCommentBox(true);

      // debugging timeout for disappearing annotations
      const timeout = setTimeout(() => {
        const annotation = document.querySelector(`[data-annotation-id*="${annotationId}"]`);
        if (!annotation) {
          const error = new Error("Annotation Disappeared");
          Sentry.captureException(error, {
            tags: {
              annotationId,
              documentId: sharedDoc.documentId,
              createCommentMobile: true,
              shareLinkPage: true,
            },
          });
        }
      }, 5000);

      // save so we can clear it if annotation is cancelled
      setAnnotationTimeout(annotationId, timeout);

      persistNewCommentAnnotation({ annotationId, selection }).catch((error) => {
        // _unsetAnnotation also clears the debug timeout
        useCommentStore.getState()._unsetAnnotation(annotationId);
        if (useCommentStore.getState().newAnnotationId === annotationId) {
          setShowNewCommentBox(false);
        }
        setToast("Could not save annotation. Please try again.");
        Sentry.captureException(error, {
          tags: {
            annotationId,
            documentId: sharedDoc.documentId,
            createCommentMobile: true,
            shareLinkPage: true,
            persistAnnotation: true,
          },
        });
      });
    },
    createComment: async (event) => {
      if (!canComment) return;

      // ProseMirror may not have finalized the selection at the moment of
      // mouseup/touchend (e.g. bottom-to-top drags on desktop, or iPadOS touch).
      // Wait one frame so the browser DOM selection stabilizes.
      await new Promise((r) => requestAnimationFrame(r));

      // Resolve the selection from the DOM — in read-only mode ProseMirror
      // does not reliably update state.selection for backward (bottom-to-top)
      // drags, and normalized Range endpoints resolve backward whole-paragraph
      // selections to block-boundary positions that /api/annotations/new
      // rejects. TextSelection.between clamps to valid text positions.
      const resolvedSelection = resolveShareLinkEditorSelection(editor.view, editor.state.doc);
      if (!resolvedSelection) return;
      const { from, to } = resolvedSelection;

      // On hybrid touch+mouse devices, touchend can be followed by a synthetic
      // mouseup. Lock briefly so we don't create two annotations for one selection.
      const now = Date.now();
      if (now - commentTriggerLockRef.current < 500) return;
      commentTriggerLockRef.current = now;

      // Position the comment box while the DOM selection is still live —
      // iPadOS often clears window.getSelection() once the native callout
      // shows, so fall back to ProseMirror coords when the range is gone.
      let bottom;
      const domSelection = window.getSelection();
      if (domSelection && domSelection.rangeCount > 0) {
        const rect = domSelection.getRangeAt(0).getBoundingClientRect();
        if (rect && rect.bottom) bottom = rect.bottom;
      }
      if (bottom == null) {
        const startCoords = editor.view.coordsAtPos(from);
        const endCoords = editor.view.coordsAtPos(to);
        bottom = Math.max(startCoords.bottom, endCoords.bottom);
      }

      // Create the annotation and open the composer immediately — persisting
      // costs up to two network round-trips (lazy anonymous sign-in + POST)
      // and shouldn't block the box appearing. Rolled back below on failure.
      const annotationId = createNewAnnotationId();
      const selection = { from, to };
      setOptimisticAnnotation(annotationId, selection);

      // Only reset on a fresh open — preserve any in-progress draft if the box
      // was already showing.
      if (!showNewCommentBox) setNewComment("");
      setNewCommentBoxLocationY(bottom);
      setShowNewCommentBox(true);

      // debugging timeout for disappearing annotations
      const timeout = setTimeout(() => {
        const annotation = document.querySelector(`[data-annotation-id*="${annotationId}"]`);

        if (!annotation) {
          const error = new Error("Annotation Disappeared");
          Sentry.captureException(error, {
            tags: {
              annotationId,
              documentId: sharedDoc.documentId,
              createComment: true,
              shareLinkPage: true,
            },
          });
        }
      }, 5000);

      // save so we can clear it if annotation is cancelled
      setAnnotationTimeout(annotationId, timeout);

      persistNewCommentAnnotation({ annotationId, selection }).catch((error) => {
        // _unsetAnnotation also clears the debug timeout
        useCommentStore.getState()._unsetAnnotation(annotationId);
        if (useCommentStore.getState().newAnnotationId === annotationId) {
          setShowNewCommentBox(false);
        }
        setToast("Could not save annotation. Please try again.");
        Sentry.captureException(error, {
          tags: {
            annotationId,
            documentId: sharedDoc.documentId,
            createComment: true,
            shareLinkPage: true,
            persistAnnotation: true,
          },
        });
      });
    },

    filterByAuthor: (author) => {
      setFilteredComments(comments.filter((comment) => comment.displayName === author));
    },
  };

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";

    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  if (sharedDocError) {
    return (
      <Layout>
        <Intro>
          <HomepageLink />
          <Title>{sharedDocError.message}</Title>
          {sharedDocError.message === "Not Found" ? (
            <p>This shared document wasn't found. If someone linked you to this page to review their work, ask them to double-check that the document is still being shared.</p>
          ) : (
            <p>Something went wrong. Please try again later.</p>
          )}
        </Intro>
      </Layout>
    );
  }

  if (sharedDocLoading) {
    return <Layout />;
  }

  const userIsAuthor = user && user.uid === sharedDoc.authorId;
  const shouldIndentParagraphs = sharedDoc?.textIndent ?? true;
  // No per-document setting: always use the 2x line-height look. Docs with
  // legacy blank spacer paragraphs just get a bit more space between them.
  const paragraphSpacing = PARAGRAPH_SPACING.X2;

  return (
    <Layout>
      <Intro className="Intro">
        {/* {console.log("🤩 [ShareLinkPage] isMobile", isMobile, sharedDoc.commentsEnabled)}
        {!isMobile && sharedDoc.commentsEnabled && (
          <UserStatus
            user={user}
            openLogin={openLogin}
          />
        )} */}
        <HomepageLink className="top-logo" />
        {showMobileOnboarding && <MobileOnboarding author={sharedDoc.author} />}
        {showDesktopOnboarding && <DesktopOnboarding author={sharedDoc.author} />}
        <Title id="document-title">{sharedDoc.title}</Title>
        {!showMobileOnboarding && (
          <p>
            {sharedDoc.author} shared this on {new Date(sharedDoc.publishedAt).toLocaleDateString(undefined, { dateStyle: "long" })}
          </p>
        )}
        {showMobileOnboarding && <p>Shared on {new Date(sharedDoc.publishedAt).toLocaleDateString(undefined, { dateStyle: "long" })}</p>}
      </Intro>
      {isMobile && sharedDoc?.commentsEnabled ? (
        <MobileLayout>
          <EditorWrapper
            className={`EditorWrapper${shouldIndentParagraphs ? " indented" : ""}`}
            paragraphSpacing={paragraphSpacing}
          >
            {editor && (
              <TiptapErrorBoundary
                editorName="ShareLinkPageEditor"
                documentId={sharedDoc?.documentId}
                userId={user?.uid || commenter?.uid || anonUser?.uid}
              >
                <EditorContent editor={editor} />
              </TiptapErrorBoundary>
            )}
          </EditorWrapper>
          {mobileTextSelected && (
            <Button
              className="MobileAddCommentButton"
              secondaryGrey
              onClick={commentHandlers.createMobileComment}
              style={{ top: `${mobileTextSelectedLocationY}px` }}
            >
              <SVG
                src="/assets/comment-icon.svg"
                aria-hidden="true"
              />
              Comment{"  "}
            </Button>
          )}
          {showNewCommentBox && (
            <NewComment
              newComment={newComment}
              setNewComment={setNewComment}
              documentId={sharedDoc.documentId}
              isMobile={true}
              onCommentCreated={setForceOpenCommentId}
            />
          )}
          {comments.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
              documentId={sharedDoc.documentId}
              isMobile={true}
              installations={installations}
              forceOpen={comment.id === forceOpenCommentId}
            />
          ))}
          {showOverlay && <Overlay onClick={() => setActiveAnnotationIds([])} />}
        </MobileLayout>
      ) : (
        <EditorAndCommentsWrapper>
          <EditorWrapper
            className={`EditorWrapper${shouldIndentParagraphs ? " indented" : ""}`}
            paragraphSpacing={paragraphSpacing}
          >
            {editor && (
              <TiptapErrorBoundary
                editorName="ShareLinkPageEditor"
                documentId={sharedDoc?.documentId}
                userId={user?.uid || commenter?.uid || anonUser?.uid}
              >
                <EditorContent
                  editor={editor}
                  onTouchEnd={commentHandlers.createComment}
                />
              </TiptapErrorBoundary>
            )}
            {showDesktopOnboardingTooltip && <DesktopOnboardingTooltip />}
          </EditorWrapper>
          {canComment && sharedDoc.commentsEnabled && (
            <CommentsBar>
              {/* {userIsAuthor && (
                <FilterButton
                  comments={comments}
                  setFiltering={setFiltering}
                  filterByAuthor={commentHandlers.filterByAuthor}
                />
              )} */}
              {showNewCommentBox && (
                <NewComment
                  newComment={newComment}
                  setNewComment={setNewComment}
                  documentId={sharedDoc.documentId}
                  commentLocation="ShareLinkPage"
                  onCommentCreated={setForceOpenCommentId}
                />
              )}
              {(filtering ? filteredComments : comments).map((comment) => (
                <Comment
                  key={comment.id}
                  comment={comment}
                  user={user}
                  commentsManager={commentsManager}
                  documentId={sharedDoc.documentId}
                  commentLocation="ShareLinkPage"
                  installations={installations}
                  forceOpen={comment.id === forceOpenCommentId}
                />
              ))}
            </CommentsBar>
          )}
        </EditorAndCommentsWrapper>
      )}
      {sharedDoc?.commentsEnabled && isMobile && (
        <Footer>
          <SignUpAd>
            <Logos
              small
              asSeenIn={false}
              vanityFair={false}
              newYorkTimes={true}
              theVerge={false}
            />
            <h1>
              <em>AUTHORS:</em> DON'T CLICK THIS BUTTON. YOU MIGHT BECOME <em>WAY TOO </em>PRODUCTIVE.
            </h1>
            <SignUpButtonContainer>
              <Button
                primary
                onClick={handleSignUp}
              >
                Sign Up Free
              </Button>
              <Caution className="first" />
              <Caution className="second" />
              <Caution className="third" />
            </SignUpButtonContainer>
          </SignUpAd>
        </Footer>
      )}
    </Layout>
  );
}

const EditorAndCommentsWrapper = styled.div({
  display: "flex",
  flexDirection: "row",
  gap: "20px",
  justifyContent: "center",
});

const Intro = styled.div`
  // Layout
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 20px;
  text-align: center;
  line-height: 1.618;

  // SVG styles
  .top-logo {
    margin-bottom: 100px;
    opacity: 0.5;
    transition: opacity 0.2s ease-in;

    &:hover {
      opacity: 1;
    }
  }

  // Typography
  h1 {
    font-size: 30px;
  }

  p {
    font-size: 20px;
    margin-top: 1em;
    max-width: 40em;
    text-align: center;
  }

  // Responsive styles
  @media (min-width: 800px) {
    padding-bottom: 100px;

    h1 {
      font-size: 50px;
      line-height: 130%;
      text-align: center;
    }
  }

  @media (max-width: 768px) {
    svg:not(.toast-icon) {
      margin-bottom: 50px;
    }

    p {
      font-size: 16px;
    }
  }
`;

const Footer = styled.div`
  display: flex;
  position: relative;
  padding-top: 20px;
  flex-direction: column;
  align-items: center;
`;

const SignUpAd = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  h1 {
    width: 90vw;
    line-height: 1.2;
    font-size: 28px;
    text-align: center;
    font-family: "GT Super Text", serif;
    font-weight: 400;
    font-style: normal;
  }
`;

const SignUpButtonContainer = styled.div`
  top: 190px;
  overflow-x: clip;
  position: absolute;
  width: 100vw;

  button {
    top: 85px;
    background-color: #27ae60;
    width: 80vw;
    height: 50px;
    max-width: 300px;
    border-radius: 10px;
  }

  button:hover {
    background-color: rgb(32, 150, 81);
  }

  button:active {
    background-color: rgb(24, 125, 64);
  }
`;

const Caution = styled.div`
  --caution-start: 56px;
  position: absolute;
  width: 130vw;
  left: -15vw;
  background-image: url("/assets/caution.svg");
  background-color: #ffd900;
  background-repeat: repeat-x;
  background-position: center;
  background-size: auto 100%;
  height: 38px;
  pointer-events: none;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.2);

  &.first {
    top: var(--caution-start);
    transform: rotate(7deg);
  }
  &.second {
    top: calc(var(--caution-start) + 82px);
    transform: rotate(-8deg);
  }
  &.third {
    top: calc(var(--caution-start) + 75px);
    transform: rotate(7deg);
  }
`;

const Title = styled.h1({
  fontFamily: '"GT Super Text", serif',
  fontStyle: "normal",
  fontWeight: 400,
  fontSize: 48,
  marginBottom: 0,
  lineHeight: "130%",
  textAlign: "center",
});

const MobileLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  .MobileAddCommentButton {
    background-color: var(--menu-color-fullscreen);
    border-radius: 8px;
    box-shadow: var(--card-drop-shadow);
    width: fit-content;
    position: fixed;
    /* bottom: 10px; */
    top: 0;
    left: 0;
    right: 0;
    margin: 0 auto;
    z-index: 1;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10;
`;

/**
 * Extra paragraph margin as (multiplier - 1) × line-height × font-size.
 * 1x → 0 (gaps match lines); share pages use a fixed 1.6 line-height.
 * Px-typed (never bare 0) because the shared heading rule divides the custom
 * property inside calc().
 */
const SHARE_LINE_HEIGHT_EM = 1.6;
const paragraphSpacingPx = (multiplier, bodyPx) => {
  const extra = Math.max(0, (multiplier || 1) - 1);
  if (!extra) return "0px";
  return `${Math.round(extra * SHARE_LINE_HEIGHT_EM * bodyPx * 100) / 100}px`;
};

const EditorWrapper = styled.div`
  // Base styles
  position: relative;
  flex-grow: 0;
  flex-shrink: 10;
  flex-basis: 800px;
  background-color: white;
  border-radius: 15px;
  box-shadow: var(--card-drop-shadow);
  word-break: break-word;
  box-sizing: border-box;
  padding: 1em 2em;

  // Typography. Font size lives on the wrapper (paragraphs inherit it) so the
  // shared editor typography rules — em-based heading ladder, drawn bullets,
  // paragraph spacing — resolve against the same base as the body text.
  text-align: initial;
  line-height: 1.6;
  font-size: 16px;
  --sw-paragraph-spacing: ${(props) => paragraphSpacingPx(props.paragraphSpacing, 16)};

  @media (min-width: 800px) {
    font-size: 20px;
    --sw-paragraph-spacing: ${(props) => paragraphSpacingPx(props.paragraphSpacing, 20)};
  }

  // Text selection
  *::selection {
    background: rgb(245, 242, 228);
  }

  // Document typography shared with the main editor: paragraph spacing via
  // --sw-paragraph-spacing, heading ladder, CSS-drawn cycling bullet markers,
  // nested-list margins and indent-via-::before (the "indented" class is set
  // from the shared doc's textIndent, mirroring #editor).
  ${editorTypographyStyles("&")}

  // Headings keep the clean-copy display serif (overrides the shared block's
  // font-family: inherit).
  h1,
  h2,
  h3 {
    font-family: "GT Super Text", serif;
    font-style: normal;
  }

  // Paragraphs
  p {
    font-family: "Georgia", serif;
    font-weight: 500;
  }

  // Drop cap
  .ProseMirror > p:first-of-type::first-letter {
    font-family: "GT Super Text", serif;
    float: left;
    font-size: 3em;
    line-height: 0.7;
    margin: 0 0.1em 0.05em 0;
  }

  // The drop-cap paragraph never indents (book convention — the shared
  // editor rules would otherwise draw the ::before indent on it too).
  &.indented .ProseMirror > p:first-of-type::before {
    content: none;
  }

  // Images
  img {
    max-width: 100%;
    display: block;
    margin: 0 auto;
  }

  // Form elements
  label textarea {
    opacity: 0;
    position: sticky;
    top: 0;
    min-width: 300px;
    padding: 30px 0 0 40px;
    background: none;
    border-width: 0;
    font-size: inherit;
    font-family: "Inter", sans-serif;
    resize: none;
    transition: opacity 0.1s ease-in;
  }

  // Editor focus state
  .ProseMirror-focused {
    outline: none;
  }

  // Responsive styles
  @media (max-width: 768px) {
    width: 100%;
    padding: 1.75em;
  }

  @media (min-width: 500px) {
    padding: 3em 60px;
    margin-left: 0;
    margin-right: 0;
  }

  @media (min-width: 600px) {
    .ProseMirror > p:first-of-type::first-letter {
      font-size: 6em;
    }
  }
`;
